import { CartRuleEngine } from 'cart-rule-engine';
import { fetchCart } from 'theme';
class CartThemeConnector {
	constructor() {
		const cartSettings = window.cartSettings || {};
		this.cartRuleEngine = new CartRuleEngine({
			rules: cartSettings.rules || [],
			successMessage: cartSettings.successMessage || '',
		});
		document.addEventListener('cart:change', this.onCartChange.bind(this));
		this.cartDrawer = document.querySelector('cart-drawer');
		this.initCartDrawerEvents();
	}
	initCartDrawerEvents() {
		if (!this.cartDrawer) {
			return;
		}
		this.cartDrawer.addEventListener('dialog:before-show', this.setupProgressBarAnimation.bind(this));
		this.cartDrawer.addEventListener('dialog:after-show', this.animateProgressBar.bind(this));
		this.cartDrawer.addEventListener('cart-drawer:refreshed', this.handleProgressBarRefresh.bind(this));
	}
	async setupProgressBarAnimation() {
		await this.updateCartBarProgress();
		this.bar = this.cartDrawer.querySelector('cart-bar');
		this.bar?.setProgressWidth(0);
	}
	animateProgressBar() {
		this.bar = this.cartDrawer.querySelector('cart-bar');
		this.bar?.animateProgressBar(0, this.cartBarProgress);
		this.oldCartBarProgress = this.cartBarProgress;
	}
	handleProgressBarRefresh() {
		this.bar = this.cartDrawer.querySelector('cart-bar');
		if (this.oldCartBarProgress === this.cartBarProgress) {
			this.bar?.setProgressWidth(this.oldCartBarProgress);
			return;
		}
		this.bar?.animateProgressBar(this.oldCartBarProgress, this.cartBarProgress);
		this.oldCartBarProgress = this.cartBarProgress;
	}
	transformPayloads(payload) {
		const updates = {};
		payload.forEach((item) => {
			updates[item.key || item.id] = item.quantity;
		});
		return updates;
	}
	updateItemsInCart({ updatePayload }) {
		const sectionsToBundle = new Set();
		document.documentElement.dispatchEvent(
			new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }),
		);
		const data = {
			updates: updatePayload,
			sections: [...sectionsToBundle].join(','),
		};
		fetch(`${Shopify.routes.root}cart/update.js`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		})
			.then(async (response) => {
				if (response.ok) {
					const cartContent = await response.json();
					// document.dispatchEvent(new CustomEvent('cart:refresh'));
					document.documentElement.dispatchEvent(
						new CustomEvent('cart:change', {
							bubbles: true,
							detail: {
								baseEvent: 'line-item:change',
								cart: cartContent,
								ignoreItemsRemoved: false,
							},
						}),
					);
				}
			})
			.catch((error) => {
				console.error('Error:', error);
			});
	}
	getRulePositions() {
		return this.cartRuleEngine.getRulePositions();
	}
	async syncCartAfterUpsellGiftChange() {
		const cart = await fetchCart;
		await this.updateValues(cart);
		if (!this.payloads?.length) {
			return;
		}
		this.updateItemsInCart({
			updatePayload: this.transformPayloads(this.payloads),
		});
	}
	async updateValues(cart) {
		const currentCart = cart ? cart : await fetchCart;
		const { qualifiedRules, payloads, formattedText, shippingPrice, cartBarProgress } =
			this.cartRuleEngine.processCartRules(currentCart);
		//this.payloads = payloads;
		const orphanChildPayloads = this.getCrossSellChildUpdates(currentCart);
		this.payloads = [...payloads, ...orphanChildPayloads];
		this.qualifiedRules = qualifiedRules;
		this.formattedText = formattedText;
		this.shippingPrice = shippingPrice;
		this.cartBarProgress = cartBarProgress;
	}
	async getFormattedText() {
		if (this.formattedText) {
			return this.formattedText;
		}
		await this.updateValues();
		return this.formattedText;
	}
	async getQualifiedRules() {
		if (this.qualifiedRules) {
			return this.qualifiedRules;
		}
		await this.updateValues();
		return this.qualifiedRules;
	}
	async updateCartBarProgress() {
		if (this.cartBarProgress) {
			await this.updateValues();
		}
	}

	simplifyCartItems(items) {
		return items.map((item) => ({
			id: item.id,
			key: item.key,
			quantity: item.quantity,
			properties: item.properties || {},
		}));
	}

	buildCrossSellGroups(items) {
		const grouped = Object.groupBy(
			items.filter((item) => item.properties?._cross_sell_id),
			(item) => item.properties._cross_sell_id,
		);

		const groups = new Map();
		for (const [crossSellId, groupItems] of Object.entries(grouped)) {
			const parent = groupItems.find((item) => item.properties?._cross_sell_role === 'parent') || null;
			const children = groupItems.filter((item) => item.properties?._cross_sell_role === 'child');

			groups.set(crossSellId, { parent, children });
		}
		return groups;
	}

	mapCrossSellGroupsToPayloads(groups) {
		const payloads = [];

		for (const { parent, children } of groups.values()) {
			if (!children.length) continue;

			if (!parent) {
				children.forEach((child) => payloads.push({ key: child.key, quantity: 0 }));
				continue;
			}

			children.forEach((child) => {
				if (child.quantity !== parent.quantity) {
					payloads.push({ key: child.key, quantity: parent.quantity });
				}
			});
		}

		return payloads;
	}
	getCrossSellChildUpdates(cart) {
		const items = cart?.items;
		if (!items || !items.length) return [];

		const simplifyCartItems = this.simplifyCartItems(items);
		const crossSellGroups = this.buildCrossSellGroups(simplifyCartItems);
		const payloads = this.mapCrossSellGroupsToPayloads(crossSellGroups);
		return payloads;
	}
	async onCartChange(event) {
		const cart = event.detail.cart;
		const nonGiftItemsRemoved = cart.items_removed?.filter((item) => parseInt(item.price) > 0);
		if (cart.items_removed?.length > 0 && nonGiftItemsRemoved?.length === 0) {
			this.cartRuleEngine.rules.forEach((rule) => {
				if (cart.items_removed.some((item) => item.variant_id === rule.giftVariantID)) {
					rule.setWasRemoved(true);
				}
			});
		} else {
			this.cartRuleEngine.rules.forEach((rule) => {
				rule.setWasRemoved(false);
			});
		}
		this.updateValues(cart);
		if (!this.payloads || this.payloads.length === 0) {
			return;
		}
		const updatePayload = this.transformPayloads(this.payloads);
		this.updateItemsInCart({
			updatePayload,
		});
	}
}
window.themeConnector = new CartThemeConnector();
export { CartThemeConnector };
// TODO DEV CODE REMOVE
// document.querySelector('#cart-drawer').show();
